import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type {
  CreateLatestVersionControlRequest,
  GetTemplateCreationRecordResponse,
  TemplateVersionControlResponse,
} from '../../contracts';
import {
  CREATE_LATEST_VERSION_CONTROL_KEY,
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
} from '../../contracts';
import { createLatestVersionControl } from '../api/templates';

export const OPTIMISTIC_VERSION_CONTROL_ID = 'optimistic-version-control';

export const useMutateCreateLatestVersionControl = ({
  onMutateComplete,
}: {
  onMutateComplete?: (params: { optimisticVersionControlId: string }) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [CREATE_LATEST_VERSION_CONTROL_KEY],
    mutationFn: async ({
      data,
      templateCreationRecordId,
      signal,
    }: {
      data: CreateLatestVersionControlRequest;
      templateCreationRecordId: string;
      signal?: AbortSignal;
    }) => {
      const { data: versionControl } = await createLatestVersionControl({
        templateCreationRecordId,
        data,
        signal,
      });
      return versionControl;
    },
    onMutate: async ({ data, templateCreationRecordId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<GetTemplateCreationRecordResponse>([
        GET_TEMPLATE_CREATION_RECORD_BY_ID,
        { templateCreationRecordId },
      ]);

      // Create optimistic version control
      const optimisticVersionControl: TemplateVersionControlResponse = {
        id: OPTIMISTIC_VERSION_CONTROL_ID,
        template_creation_record_id: templateCreationRecordId,
        template_structure: data.template_structure ?? '',
        chat_query: data.chat_query ?? null,
        example_note: data.example_note ?? null,
        created_at: new Date().toISOString(),
      };

      // Optimistically update the cache
      queryClient.setQueryData<GetTemplateCreationRecordResponse>(
        [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            draft.version_controls.push(optimisticVersionControl);
          });
        }
      );

      onMutateComplete?.({ optimisticVersionControlId: OPTIMISTIC_VERSION_CONTROL_ID });

      return { previousData };
    },
    onSuccess: (versionControl, { templateCreationRecordId }) => {
      // Replace the optimistic version control with the real one
      queryClient.setQueryData<GetTemplateCreationRecordResponse>(
        [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            const optimisticIndex = draft.version_controls.findIndex(
              (vc) => vc.id === OPTIMISTIC_VERSION_CONTROL_ID
            );
            if (optimisticIndex !== -1) {
              draft.version_controls[optimisticIndex] = versionControl;
            } else {
              // Fallback: push if optimistic not found
              draft.version_controls.push(versionControl);
            }
          });
        }
      );
    },
    onError: (error: Error, { templateCreationRecordId }, context) => {
      logger.error('Failed to create latest version control', error);

      // Rollback to previous data if available
      if (context?.previousData) {
        queryClient.setQueryData<GetTemplateCreationRecordResponse>(
          [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
          context.previousData
        );
      } else {
        // Fallback: pop the optimistic version control
        queryClient.setQueryData<GetTemplateCreationRecordResponse>(
          [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }

            return produce(oldData, (draft) => {
              const optimisticIndex = draft.version_controls.findIndex(
                (vc) => vc.id === OPTIMISTIC_VERSION_CONTROL_ID
              );
              if (optimisticIndex !== -1) {
                draft.version_controls.splice(optimisticIndex, 1);
              }
            });
          }
        );
      }
    },
  });
};
